import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { InfiniteScrollProps } from '../../types'

// Skeleton message component
const SkeletonMessage: React.FC<{ index: number }> = ({ index }) => {
  const isOutgoing = index % 3 === 0 // Mix of incoming and outgoing
  const animationDelay = `${index * 100}ms`

  return (
    <div
      className={`mb-4 flex items-start gap-2 ${isOutgoing ? 'flex-row-reverse' : 'flex-row'}`}
    >
      <div
        className="w-8 h-8 bg-gray-200 dark:bg-muted rounded-full animate-pulse flex-shrink-0"
        style={{ animationDelay }}
      />

      <div
        className={`max-w-[70%] flex flex-col gap-1 ${isOutgoing ? 'items-end' : 'items-start'}`}
      >
        {!isOutgoing && (
          <div
            className="w-20 h-3 bg-gray-300 dark:bg-muted rounded-sm mb-0.5 animate-pulse"
            style={{ animationDelay }}
          />
        )}

        <div
          className={`
            animate-pulse bg-gray-100 dark:bg-muted rounded-2xl px-4 py-3
            ${
              index % 4 === 0
                ? 'w-72'
                : index % 4 === 1
                  ? 'w-52'
                  : index % 4 === 2
                    ? 'w-40'
                    : 'w-64'
            }
            h-10
          `}
          style={{ animationDelay }}
        />

        <div
          className="w-12 h-2 bg-gray-200 dark:bg-muted/60 rounded-sm mt-1 animate-pulse self-end"
          style={{ animationDelay: `${index * 100 + 50}ms` }}
        />
      </div>
    </div>
  )
}

export const InfiniteList: React.FC<InfiniteScrollProps> = props => {
  const {
    messages,
    hasMore,
    loading,
    renderMessage,
    className = '',
    oldestMessageRef,
    newestMessageRef,
    isDeeplinked = false,
    hasMoreNewer = false,
    loadingNewer = false,
  } = props

  // Show skeleton when initially loading (no messages yet)
  const showSkeleton = loading && messages.length === 0

  return (
    <div
      className={`flex flex-col-reverse min-h-full mb-[200px] px-6 ${className}`}
      data-testid="message-list-container"
    >
      <AnimatePresence>
        {showSkeleton && (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {Array.from({ length: 8 }, (_, index) => (
              <SkeletonMessage key={`skeleton-${index}`} index={index} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {messages.length > 0 && !showSkeleton && (
          <motion.div
            key="messages"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
          >
            {messages.map((message, index) => {
              const isOldest = index === 0
              const isNewest = index === messages.length - 1
              let ref: React.RefObject<HTMLDivElement | null> | undefined

              if (isOldest && oldestMessageRef) {
                ref = oldestMessageRef
              } else if (isNewest && newestMessageRef && isDeeplinked) {
                ref = newestMessageRef
              }

              return (
                <motion.div
                  key={message.id}
                  ref={ref}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.01,
                  }}
                >
                  {renderMessage(message, index)}
                </motion.div>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSkeleton && (
          <motion.div
            key="center-skeleton"
            className="flex justify-center items-center py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="w-48 h-4 bg-gray-200 dark:bg-muted rounded animate-pulse" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading indicator for older messages (backwards scrolling) */}
      <motion.div
        className="flex justify-center py-2"
        initial={{ opacity: 0 }}
        animate={{
          opacity:
            hasMore && messages.length > 0 && loading && !showSkeleton ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          pointerEvents:
            hasMore && messages.length > 0 && loading && !showSkeleton
              ? 'auto'
              : 'none',
        }}
      >
        <div className="flex items-center">
          <div className="inline-block w-3 h-3 border-2 border-gray-300 dark:border-muted border-t-gray-700 dark:border-t-foreground rounded-full animate-spin" />
          <span className="ml-2 text-sm text-gray-500 dark:text-muted-foreground">
            Loading older messages...
          </span>
        </div>
      </motion.div>

      {/* Loading indicator for newer messages (forwards scrolling when deeplinked) */}
      {isDeeplinked && hasMoreNewer && loadingNewer && messages.length > 0 && (
        <motion.div
          className="flex justify-center py-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center">
            <div className="inline-block w-3 h-3 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
            <span className="ml-2 text-sm text-gray-500">
              Loading newer messages...
            </span>
          </div>
        </motion.div>
      )}
    </div>
  )
}

InfiniteList.displayName = 'InfiniteList'
